/**
 * Block 14 «Раскрой» — палитра SVG-карты раскроя (PR #22, CUT-5.2, EP-5).
 *
 * Цвет детали на карте — по МАТЕРИАЛУ (одинаковый декор = один цвет; STOP-pre #2):
 * для мульти-материального проекта детали разных декоров видно по цвету, а
 * экземпляры одного декора — единым цветом. Палитра — вербатим multi-режима
 * прототипа (`project.html:471-473` swatches). Маппинг детерминирован (тот же
 * ключ → тот же цвет, без `Math.random`), чтобы карта была воспроизводимой.
 *
 * `partId` размещения — это `instanceId` движка вида `${part.id}#${k}`
 * (`cutting-engine/src/optimize.ts:288`); `basePartId` срезает суффикс `#k`,
 * чтобы найти исходную деталь и её материал.
 */

import type { Part } from '../model/types';

/** Фикс-палитра карты (вербатим swatches multi-режима прототипа + расширение). */
export const CUT_MAP_PALETTE = [
  '#ef4444', // red — прототип multi #1
  '#10b981', // green — прототип multi #2
  '#3b82f6', // blue — прототип multi #3
  '#facc15', // yellow — прототип multi #4
  '#a855f7', // purple
  '#f97316', // orange
  '#14b8a6', // teal
  '#ec4899', // pink
] as const;

/**
 * Базовый id детали из `instanceId` движка `${part.id}#${k}` — срез по первому
 * `#`. Если `#` нет (на всякий случай) — строка возвращается как есть.
 */
export function basePartId(instanceId: string): string {
  const i = instanceId.indexOf('#');
  return i === -1 ? instanceId : instanceId.slice(0, i);
}

/** Детерминированный 32-бит хеш строки (FNV-1a). Не зависит от платформы/рантайма. */
function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Цвет по ключу материала: тот же ключ → тот же цвет палитры (детерминирован). */
export function colorForMaterial(materialKey: string): string {
  const i = hashString(materialKey) % CUT_MAP_PALETTE.length;
  return CUT_MAP_PALETTE[i] ?? CUT_MAP_PALETTE[0];
}

/**
 * Цвет размещённой детали: `instanceId` → базовый id → деталь → её `material`
 * (free-text декора, P-001). Пустой/неизвестный материал → единый ключ `''`
 * (одно-материальный проект = один цвет — принятый компромисс STOP-pre #2).
 */
export function resolvePartColor(instanceId: string, parts: Part[]): string {
  const base = basePartId(instanceId);
  const part = parts.find((p) => p.id === base);
  const key = part?.material?.trim() ?? '';
  return colorForMaterial(key);
}
